// TransformLayerOrientationCommand — Edit > Transform > Flip Horizontal /
// Flip Vertical / Rotate 180° / Rotate 90° CW / Rotate 90° CCW (audit i24).
//
// Unlike Image > Image Rotation (RotateImageCommand), which rewrites the
// WHOLE document (canvas dimensions + every layer), this command transforms
// only the given layer id(s) — the active layer, or every selected layer
// when the caller passes more than one id (Batch 9 multi-layer convention,
// see MoveLayersCommand). Each layer keeps its own bounding box anchored in
// place: flip/180° leave the box where it is (dimensions unchanged), and the
// two 90° rotations re-centre the (possibly width/height-swapped) box on the
// original box's centre point, matching Photoshop's per-layer transform
// anchor instead of resetting the layer to the canvas origin.
//
// The four ops are lossless index permutations (see
// core/pixel/orientationTransform.ts, shared with RotateImageCommand), so
// `invert()` doesn't need to snapshot pixel data — it just re-applies the
// algebraic inverse op (flip-h/flip-v/180 are self-inverse; 90-cw/90-ccw
// invert each other).

import type { Document } from "../document/Document";
import type { Layer, RasterLayer } from "../document/Layer";
import { PixelBuffer } from "../pixel/PixelBuffer";
import { invertOrientationOp, rotateOrientationBuffer, type OrientationOp } from "../pixel/orientationTransform";
import type { Command } from "./Command";

export class TransformLayerOrientationCommand implements Command {
  readonly kind = "transform-layer-orientation";

  constructor(
    private readonly layerIds: readonly string[],
    private readonly op: OrientationOp,
  ) {}

  apply(doc: Document): Document {
    let layers = doc.layers;
    for (const id of this.layerIds) {
      layers = applyOrientation(layers, id, this.op);
    }
    return { ...doc, layers };
  }

  invert(_before: Document, _after: Document): Command {
    return new TransformLayerOrientationCommand(this.layerIds, invertOrientationOp(this.op));
  }
}

// ---------------------------------------------------------------------------
// Edit > Transform > Again (Shift+Ctrl+T, audit i25)
// ---------------------------------------------------------------------------
//
// `main.ts` records the last COMMITTED transform in a module-level
// `lastTransform: LastTransformRecord | null` and calls `planTransformAgain`
// from its `transformAgain()` handler. The decision logic lives here (pure,
// unit-testable) instead of inline in main.ts, which has no unit tests of
// its own (it's the DOM-wiring entry point).

/**
 * What the host recorded as "the last transform" — set after a discrete
 * Edit > Transform flip/rotate commits, or after a Free Transform commit.
 */
export type LastTransformRecord =
  | { readonly kind: "layer-orientation"; readonly op: OrientationOp }
  | { readonly kind: "free-transform" };

export type TransformAgainPlan =
  | { readonly kind: "command"; readonly command: TransformLayerOrientationCommand }
  | { readonly kind: "error"; readonly message: string };

/**
 * Decide what Edit > Transform > Again should do, given the last recorded
 * transform and the raster layer id(s) it should now target (the CURRENT
 * active/selected layers — Photoshop's Transform Again re-targets whatever
 * is active now, not necessarily the layer it was first applied to).
 *
 * Returns null when there's nothing to repeat (caller shows its own
 * "No transform to repeat" message, matching the pre-fix behaviour).
 *
 * A `free-transform` record produces an explicit `"error"` plan rather than
 * silently no-op'ing or faking a replay: interactive scale/rotate/skew/
 * distort/perspective drags aren't reconstructable from the committed
 * TransformLayerCommand alone (there's no recorded "again" gesture), and
 * building that out depends on the interactive-drag mechanics tracked
 * separately under audit i20. That's flagged here as a follow-up, not
 * implemented in this fix.
 */
export function planTransformAgain(
  record: LastTransformRecord | null,
  targetLayerIds: readonly string[],
): TransformAgainPlan | null {
  if (!record) return null;
  if (record.kind === "free-transform") {
    return {
      kind: "error",
      message:
        "Repeating an interactive Free Transform isn't supported yet (see audit i20) — reapply it manually via Edit > Free Transform.",
    };
  }
  if (targetLayerIds.length === 0) {
    return { kind: "error", message: "No active raster layer to transform." };
  }
  return { kind: "command", command: new TransformLayerOrientationCommand(targetLayerIds, record.op) };
}

// ---------------------------------------------------------------------------
// Tree helpers
// ---------------------------------------------------------------------------

function applyOrientation(layers: readonly Layer[], id: string, op: OrientationOp): Layer[] {
  return layers.map((l) => {
    if (l.id === id && l.kind === "raster") {
      return orientRasterLayer(l, op);
    }
    if (l.kind === "group") {
      return { ...l, children: applyOrientation(l.children, id, op) };
    }
    return l;
  });
}

function orientRasterLayer(layer: RasterLayer, op: OrientationOp): RasterLayer {
  const src = layer.pixels;
  const out = rotateOrientationBuffer(src.data, src.width, src.height, op);
  const buf = new PixelBuffer(out.width, out.height, src.mode);
  buf.data.set(out.data);
  buf.markDirty();
  const { x, y } = anchoredPosition(layer.x, layer.y, src.width, src.height, out.width, out.height);
  return { ...layer, x, y, pixels: buf };
}

/**
 * Where the layer's top-left should land after reorientation so its
 * bounding box stays anchored the way Photoshop keeps a single layer put
 * under Edit > Transform.
 *
 * Flip / 180° don't change width/height, so the box doesn't move at all.
 * The two 90° rotations swap width/height, so we re-centre the new box on
 * the old box's centre. Note: if width and height differ by an odd amount,
 * the true centre falls on a half-pixel and Math.round can leave a ±1px
 * anchor drift after repeated 90° turns — the same integer-position
 * constraint every other layer-position command here (MoveLayerCommand,
 * TransformLayerCommand) already operates under.
 */
function anchoredPosition(
  x: number,
  y: number,
  w: number,
  h: number,
  newW: number,
  newH: number,
): { x: number; y: number } {
  if (w === newW && h === newH) return { x, y };
  const cx = x + w / 2;
  const cy = y + h / 2;
  return { x: Math.round(cx - newW / 2), y: Math.round(cy - newH / 2) };
}
